import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { fetchRecipeByHandle, fetchRecipesFromShopify } from '../redux/actions/recipeActions';
import { Recipe } from '../types/shopify';
import { ReduxState } from '../types/redux';
import GrocerizeLoadingWheel from '../components/grocerize/GrocerizeLoadingWheel';
import BlogDetailTemplate from '../components/templates/BlogDetailTemplate';
import PageTemplate from './PageTemplate';

interface BlogDetailProps {
  fetchRecipeByHandle: (blogHandle: string, recipeHandle: string) => Promise<Recipe | null>;
  fetchRecipesFromShopify: (blogHandle?: string, cursor?: string) => Promise<Recipe[]>;
  currentRecipe: Recipe | null;
  shopifyRecipes: Recipe[];
  isLoading: boolean;
  error: string | null;
}

const BlogDetail: React.FC<BlogDetailProps> = ({
  fetchRecipeByHandle,
  fetchRecipesFromShopify,
  currentRecipe,
  shopifyRecipes,
  isLoading,
  error
}) => {
  const { handle } = useParams<{ handle: string }>();
  const [latestPostsLoading, setLatestPostsLoading] = useState(false);
  
  // Function to get the current blog handle from sessionStorage
  const getBlogHandle = () => {
    // Try to get the blog handle from sessionStorage
    const storedBlogHandle = sessionStorage.getItem('current_blog_handle');
    // Use stored handle if available, otherwise default to 'recipes'
    const blogHandle = storedBlogHandle || 'recipes';
    console.log(`Using blog handle from sessionStorage: ${blogHandle}`);
    return blogHandle;
  };
  
  useEffect(() => {
    if (handle) {
      // Get the current blog handle from sessionStorage
      const blogHandle = getBlogHandle();
      
      // Fetch the blog post when component mounts or handle changes
      fetchRecipeByHandle(blogHandle, handle);
      
      // Fetch latest posts for the sidebar from the same blog category
      setLatestPostsLoading(true);
      fetchRecipesFromShopify(blogHandle).then(() => {
        setLatestPostsLoading(false);
      });
    }
  }, [fetchRecipeByHandle, fetchRecipesFromShopify, handle]);
  
  if (isLoading) {
    return (
      <div className="container u-flex u-justify-center u-p--32">
        <GrocerizeLoadingWheel />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container u-p--32">
        <div className="u-background--red u-color-white u-p--16 u-br--small">
          <p className="u-main-font--medium">{error}</p>
        </div>
        <div className="u-pt--16">
          <Link to="/browse/blogs" className="u-color-magenta u-main-font--medium">
            &larr; Back to Blogs
          </Link>
        </div>
      </div>
    );
  }
  
  if (!currentRecipe) {
    return (
      <div className="container u-p--32">
        <p className="u-main-font--large">Blog post not found</p>
        <div className="u-pt--16">
          <Link to="/browse/blogs" className="u-color-magenta u-main-font--medium">
            &larr; Back to Blogs
          </Link>
        </div>
      </div>
    );
  }
  
  // Get latest posts excluding the current one
  const latestPosts = shopifyRecipes
    .filter(post => post.handle !== handle)
    .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
    .slice(0, 4); // Get only the 4 most recent posts

  return (
    <PageTemplate sidebarType="none" headerType="magenta" hideItemSearching={true}>
      <div className="u-background--lightgrey">
        <div className="container">
          {currentRecipe && (
            <BlogDetailTemplate
              title={currentRecipe.title}
              publishedAt={currentRecipe.publishedAt}
              author={currentRecipe.author}
              image={currentRecipe.image}
              excerpt={currentRecipe.excerpt}
              contentHtml={currentRecipe.contentHtml}
              tags={currentRecipe.tags}
              backLink="/browse/blogs"
              backLinkText="Back to Blogs"
              latestPosts={latestPostsLoading ? [] : latestPosts}
            />
          )}
        </div>
      </div>
    </PageTemplate>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  currentRecipe: state.recipes.currentRecipe,
  shopifyRecipes: state.recipes.shopifyRecipes || [],
  isLoading: state.recipes.isLoading,
  error: state.recipes.error
});

const mapDispatchToProps = {
  fetchRecipeByHandle,
  fetchRecipesFromShopify
};

export default connect(mapStateToProps, mapDispatchToProps)(BlogDetail);
